import type { Feature, FeatureCollection } from 'geojson';
import { useI18n } from 'vue-i18n';

import type { DrawLayerResponseDTO } from '@nora-monorepo/nora-api-client';
import { DRAW_LAYER_NAME_MAX_LENGTH, isNonEmptyString } from '@nora-monorepo/shared';
import { useTextPromptModalStore, useToastStore } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useDrawLayerApiStore } from '#nora-ui/stores/api/draw-layer-api-store';

export interface DraftDrawLayerCreationTarget {
  map?: NoraMap;
  operationId?: string;
}

const geoJsonMimeType = 'application/geo+json';

function isGeoJsonFile(file: File): boolean {
  return file.type === geoJsonMimeType || file.name.toLowerCase().endsWith('.geojson');
}

function parseGeoJsonFeatures(fileContent: string): Feature[] {
  const geoJson = JSON.parse(fileContent) as FeatureCollection;

  return geoJson.features;
}

async function readFileAsText(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error(`Failed to read GeoJSON file ${file.name}`));

        return;
      }

      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read GeoJSON file ${file.name}`));
    };

    reader.readAsText(file);
  });
}

export function useDraftDrawLayerCreation() {
  const mapStore = useMapStore();
  const drawLayerApiStore = useDrawLayerApiStore();
  const textPromptModalStore = useTextPromptModalStore();
  const toastStore = useToastStore();
  const { t } = useI18n();

  function resolveTargetMap(target: DraftDrawLayerCreationTarget): NoraMap | undefined {
    if (target.map !== undefined) {
      return target.map;
    }

    if (!isNonEmptyString(target.operationId)) {
      return undefined;
    }

    return mapStore.getMapByOperationId(target.operationId);
  }

  function showCreateDraftLayerPrompt(target: DraftDrawLayerCreationTarget, uploadedFeatures: Feature[] = []): void {
    const operationId = target.operationId;

    if (!isNonEmptyString(operationId)) {
      return;
    }

    textPromptModalStore.showTextPromptModal({
      title: 'Draw layer',
      textFieldLabel: 'Draw layer name',
      placeholder: t('conversationDialog.titlePlaceholder'),
      confirmButtonLabel: t('common.save'),
      cancelButtonLabel: t('common.cancel'),
      requireNonEmptyValue: true,
      nonEmptyValueErrorMessage: t('operation.validation.drawLayerNameRequired'),
      maxCharacters: DRAW_LAYER_NAME_MAX_LENGTH,
      onConfirm: (layerName: string) => {
        void createDraftLayer(layerName, operationId, target, uploadedFeatures);
      },
    });
  }

  async function addDrawLayerToVisibleMaps(drawLayer: DrawLayerResponseDTO): Promise<void> {
    const addDrawLayerTasks: Promise<void>[] = [];

    for (const map of mapStore.maps) {
      addDrawLayerTasks.push(map.state.draw.addDrawLayer(drawLayer));
    }

    await Promise.allSettled(addDrawLayerTasks);
  }

  async function createDraftLayer(
    layerName: string,
    operationId: string,
    target: DraftDrawLayerCreationTarget,
    uploadedFeatures: Feature[],
  ): Promise<void> {
    try {
      const features = uploadedFeatures.map(feature => JSON.stringify(feature));
      const drawLayer = await drawLayerApiStore.createDrawLayer({
        name: layerName.trim(),
        features,
        draft: true,
      }, operationId);

      if (!drawLayer) {
        throw new Error('Failed to create draw layer');
      }

      // Show the new layer on all maps without waiting for the server's create event
      await addDrawLayerToVisibleMaps(drawLayer);

      const targetMap = resolveTargetMap(target);

      if (targetMap === undefined) {
        return;
      }

      targetMap.state.draw.setActiveDrawLayer(drawLayer.id);
    }
    catch {
      toastStore.addError({
        message: t('drawLayer.createDrawLayerFailedTitle'),
        detail: t('drawLayer.createDrawLayerFailedMessage'),
      });
    }
  }

  async function importGeoJsonFile(file: File, target: DraftDrawLayerCreationTarget): Promise<void> {
    try {
      const fileContent = await readFileAsText(file);
      const uploadedFeatures = parseGeoJsonFeatures(fileContent);

      showCreateDraftLayerPrompt(target, uploadedFeatures);
    }
    catch {
      toastStore.addError({
        message: t('drawLayer.createDrawLayerFailedTitle'),
        detail: t('drawLayer.createDrawLayerFailedMessage'),
      });
    }
  }

  function importGeoJsonFiles(files: Iterable<File>, target: DraftDrawLayerCreationTarget): void {
    for (const file of files) {
      if (!isGeoJsonFile(file)) {
        continue;
      }

      void importGeoJsonFile(file, target);
    }
  }

  return {
    showCreateDraftLayerPrompt,
    importGeoJsonFiles,
  };
}
